import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import CursorLayer from '../components/common/CursorLayer';
import Reveal from '../components/common/Reveal';
import SectionHeading from '../components/common/SectionHeading';
import FileDropzone from '../components/FileDropzone';
import OrderSuccessModal from '../components/OrderSuccessModal';
import { useToast } from '../components/common/ToastProvider';
import { appConfig, hasApiConfig, isCashfreeLive } from '../config/artTasteConfig';
import {
  defaultOrderState,
  heroStats,
  marqueeItems,
  navLinks,
  pricingPlans,
  services,
  testimonials,
  workflowSteps,
} from '../data/content';
import useCountdown from '../hooks/useCountdown';
import { formatCurrency } from '../lib/format';
import {
  bindingOptions,
  calculateOrderPricing,
  deliveryOptions,
  paperSizeOptions,
  printTypeOptions,
} from '../lib/pricing';
import { getFilePageCount } from '../lib/filePageCount';
import {
  getFriendlyErrorMessage,
  submitOrder,
  isAuthenticated,
  getCurrentUser,
  signOutUser,
} from '../lib/artTasteApi';

function cloneDefaultOrderState() {
  return {
    customer: { ...defaultOrderState.customer },
    printOptions: { ...defaultOrderState.printOptions },
    notes: defaultOrderState.notes,
  };
}

function getSetupBanner() {
  if (!hasApiConfig) {
    return {
      tone: 'warning',
      text:
        'Backend API is not configured yet. Add REACT_APP_API_URL to Art-Taste/.env before placing live test orders.',
    };
  }

  if (!isCashfreeLive) {
    return {
      tone: 'info',
      text:
        'Cashfree is currently disabled on the storefront, so orders will be stored in demo mode without live payment capture.',
    };
  }

  return null;
}

export default function HomePage() {
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [orderForm, setOrderForm] = useState(() => cloneDefaultOrderState());
  const [fieldErrors, setFieldErrors] = useState({
    name: '',
    phone: '',
  });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [filesUploaded, setFilesUploaded] = useState(false);
  const [currentOrderId, setCurrentOrderId] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({
    visible: false,
    currentFile: '',
    fileIndex: 0,
    totalFiles: 0,
    overallProgress: 0,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState('');
  const [notifyLocked, setNotifyLocked] = useState(false);
  const [successState, setSuccessState] = useState({
    isOpen: false,
    orderId: '',
    paymentMode: 'demo',
  });
  const countdown = useCountdown(appConfig.storeLaunchDate);
  const { showToast } = useToast();
  const pricing = calculateOrderPricing(orderForm.printOptions);
  const setupBanner = getSetupBanner();
  const currentUser = getCurrentUser();

  const validateCustomerField = (field, value) => {
    if (field === 'name') {
      if (!value.trim()) {
        return '';
      }
      if (value.trim().length < 3) {
        return 'Name must be at least 3 characters long.';
      }
    }

    if (field === 'phone') {
      if (!value) {
        return '';
      }
      if (!/^\d+$/.test(value)) {
        return 'Phone number can contain digits only.';
      }
      if (value.length < 10) {
        return 'Phone number must be at least 10 digits.';
      }
    }

    return '';
  };

  const handleCustomerChange = (field) => (event) => {
    const rawValue = event.target.value;
    const value = field === 'phone' ? rawValue.replace(/\D/g, '') : rawValue;
    const nextError =
      field === 'phone' && rawValue !== value
        ? 'Phone number can contain digits only.'
        : validateCustomerField(field, value);

    setOrderForm((currentForm) => ({
      ...currentForm,
      customer: {
        ...currentForm.customer,
        [field]: value,
      },
    }));

    if (field === 'name' || field === 'phone') {
      setFieldErrors((currentErrors) => ({
        ...currentErrors,
        [field]: nextError,
      }));
    }
  };

  const handlePrintOptionChange = (field) => (event) => {
    const isNumericField = field === 'copies' || field === 'pages';
    const numericValue = Number(event.target.value || 0);
    const nextValue = isNumericField
      ? Math.max(
        1,
        Math.min(
          field === 'copies' ? appConfig.maxCopies : appConfig.maxPages,
          numericValue || 1,
        ),
      )
      : event.target.value;

    setOrderForm((currentForm) => ({
      ...currentForm,
      printOptions: {
        ...currentForm.printOptions,
        [field]: nextValue,
      },
    }));
  };

  const handleNotesChange = (event) => {
    const value = event.target.value;
    setOrderForm((currentForm) => ({
      ...currentForm,
      notes: value,
    }));
  };

  const syncPagesFromFiles = (files) => {
    const totalPages = files.reduce((sum, file) => sum + Number(file.pageCount || 0), 0);

    setOrderForm((currentForm) => ({
      ...currentForm,
      printOptions: {
        ...currentForm.printOptions,
        pages: Math.max(1, totalPages || 1),
      },
    }));
  };

  const addFiles = async (incomingFiles) => {
    if (!isAuthenticated()) {
      showToast('Please sign up or login to upload files.', 'error');
      // Redirect to auth page
      window.location.href = '/auth';
      return;
    }

    if (!validateFormDetails()) {
      return;
    }

    const dedupedFiles = [];

    incomingFiles.forEach((file) => {
      if (file.size > appConfig.maxFileSizeBytes) {
        showToast(
          `${file.name} is larger than 100MB. Please compress it before uploading.`,
          'error',
        );
        return;
      }

      const alreadySelected =
        selectedFiles.some(
          (selectedFile) =>
            selectedFile.name === file.name &&
            selectedFile.size === file.size &&
            selectedFile.lastModified === file.lastModified,
        ) ||
        dedupedFiles.some(
          (selectedFile) =>
            selectedFile.name === file.name &&
            selectedFile.size === file.size &&
            selectedFile.lastModified === file.lastModified,
        );

      if (!alreadySelected) {
        dedupedFiles.push(file);
      }
    });

    if (dedupedFiles.length > 0) {
      try {
        const filesWithPageCounts = [];

        for (const file of dedupedFiles) {
          const pageCount = await getFilePageCount(file);
          filesWithPageCounts.push(Object.assign(file, { pageCount }));
        }

        setSelectedFiles((currentFiles) => {
          const nextFiles = [...currentFiles, ...filesWithPageCounts];
          syncPagesFromFiles(nextFiles);
          return nextFiles;
        });
      } catch (error) {
        showToast(error.message || 'Could not read page counts from the selected files.', 'error');
      }
    }
  };

  const removeFile = (index) => {
    setSelectedFiles((currentFiles) => {
      const nextFiles = currentFiles.filter((_, currentIndex) => currentIndex !== index);
      syncPagesFromFiles(nextFiles);
      return nextFiles;
    });
  };

  const handleUploadFiles = async () => {
    if (!isAuthenticated()) {
      showToast('Please sign up or login to upload files.', 'error');
      window.location.href = '/auth';
      return;
    }

    if (!validateFormDetails()) {
      return;
    }

    if (selectedFiles.length === 0) {
      showToast('Please select files to upload.', 'error');
      return;
    }

    setIsUploading(true);

    try {
      const orderId = currentOrderId || `AT-${Date.now()}`;
      const result = await submitOrder({
        orderInfo: {
          orderId,
          customer: orderForm.customer,
          printOptions: {
            ...orderForm.printOptions,
            copies: Number(orderForm.printOptions.copies),
            pages: Number(orderForm.printOptions.pages),
          },
          notes: orderForm.notes.trim(),
          totalAmount: pricing.total,
        },
        files: selectedFiles,
        onProgress: setUploadProgress,
        uploadOnly: true, // New flag to indicate upload-only mode
      });

      setCurrentOrderId(result.orderId);
      setUploadedFiles(result.uploadedFiles || []);
      setFilesUploaded(true);
      showToast('Files uploaded successfully.', 'success');
    } catch (error) {
      showToast(getFriendlyErrorMessage(error, 'Failed to upload files.'), 'error');
    } finally {
      setIsUploading(false);
    }
  };

  const resetOrderState = () => {
    setOrderForm(cloneDefaultOrderState());
    setFieldErrors({
      name: '',
      phone: '',
    });
    setSelectedFiles([]);
    setUploadedFiles([]);
    setFilesUploaded(false);
    setCurrentOrderId(null);
    setUploadProgress({
      visible: false,
      currentFile: '',
      fileIndex: 0,
      totalFiles: 0,
      overallProgress: 0,
    });
  };

  const validateFormDetails = () => {
    const requiredFields = [
      { value: orderForm.customer.name, label: 'full name' },
      { value: orderForm.customer.phone, label: 'phone number' },
      { value: orderForm.customer.email, label: 'email address' },
      { value: orderForm.customer.address, label: 'delivery address' },
    ];

    const missingField = requiredFields.find((field) => !field.value.trim());

    if (missingField) {
      showToast(`Please enter your ${missingField.label} before uploading files.`, 'error');
      return false;
    }

    const nameError = validateCustomerField('name', orderForm.customer.name);
    const phoneError = validateCustomerField('phone', orderForm.customer.phone);
    if (nameError || phoneError) {
      setFieldErrors((currentErrors) => ({
        ...currentErrors,
        name: nameError,
        phone: phoneError,
      }));
      showToast(nameError || phoneError, 'error');
      return false;
    }

    if (!/\S+@\S+\.\S+/.test(orderForm.customer.email)) {
      showToast('Please enter a valid email address before uploading files.', 'error');
      return false;
    }

    return true;
  };

  const validateForm = () => {
    if (!validateFormDetails()) {
      return false;
    }

    if (!filesUploaded || uploadedFiles.length === 0) {
      showToast('Please upload your files before placing the order.', 'error');
      return false;
    }

    return true;
  };

  const handleNotifySubmit = (event) => {
    event.preventDefault();

    if (!/\S+@\S+\.\S+/.test(notifyEmail)) {
      showToast('Enter a valid email so we can notify you.', 'error');
      return;
    }

    setNotifyLocked(true);
    setNotifyEmail('');
    showToast('You are on the list for the store launch update.', 'success');
  };

  const handleOrderSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await submitOrder({
        orderInfo: {
          orderId: currentOrderId,
          customer: orderForm.customer,
          printOptions: {
            ...orderForm.printOptions,
            copies: Number(orderForm.printOptions.copies),
            pages: Number(orderForm.printOptions.pages),
          },
          notes: orderForm.notes.trim(),
          totalAmount: pricing.total,
        },
        files: uploadedFiles, // Use uploaded files instead of selected files
        onProgress: setUploadProgress,
        paymentOnly: true, // New flag to indicate payment-only mode
      });

      setSuccessState({
        isOpen: true,
        orderId: result.orderId,
        paymentMode: result.paymentMode,
      });
      showToast('Order placed successfully.', 'success');
      resetOrderState();
    } catch (error) {
      showToast(
        getFriendlyErrorMessage(error, 'Something went wrong while placing your order.'),
        'error',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="art-page-shell">
      <CursorLayer />

      {setupBanner ? (
        <div className={`setup-banner tone-${setupBanner.tone}`}>
          <span>{setupBanner.text}</span>
        </div>
      ) : null}

      <header className="site-header">
        <a href="#home" className="brand-mark">
          Art<span>-</span>Taste
        </a>
        <button
          type="button"
          className={`mobile-nav-toggle ${isMobileNavOpen ? 'active' : ''}`}
          aria-label={isMobileNavOpen ? 'Close navigation menu' : 'Open navigation menu'}
          aria-expanded={isMobileNavOpen}
          onClick={() => setIsMobileNavOpen((open) => !open)}
        >
          <span />
          <span />
          <span />
        </button>
        <div
          className={`mobile-nav-backdrop ${isMobileNavOpen ? 'open' : ''}`}
          onClick={() => setIsMobileNavOpen(false)}
          role="presentation"
        />
        <nav className={`site-nav ${isMobileNavOpen ? 'open' : ''}`} aria-label="Primary">
          <div className="mobile-nav-header">
            <span className="mobile-nav-title">Menu</span>
            <button
              type="button"
              className="mobile-nav-close"
              aria-label="Close navigation menu"
              onClick={() => setIsMobileNavOpen(false)}
            >
              <span />
              <span />
            </button>
          </div>
          {navLinks.map((link) => {
            // Skip auth link if user is already authenticated
            if (link.href === '/auth' && currentUser) {
              return null;
            }
            return link.href.startsWith('/') ? (
              <Link
                key={link.href}
                to={link.href}
                className={link.emphasized ? 'nav-link nav-link-cta' : 'nav-link'}
                onClick={() => setIsMobileNavOpen(false)}
              >
                {link.label}
              </Link>
            ) : (
              <a
                key={link.href}
                href={link.href}
                className={link.emphasized ? 'nav-link nav-link-cta' : 'nav-link'}
                onClick={() => setIsMobileNavOpen(false)}
              >
                {link.label}
              </a>
            );
          })}
          {currentUser && (
            <div className="user-menu">
              <span className="user-greeting">Hi, {currentUser.name}</span>
              <Link to="/dashboard" className="nav-link">
                Dashboard
              </Link>
              {currentUser.role === 'admin' && (
                <Link to="/admin" className="nav-link" onClick={() => setIsMobileNavOpen(false)}>
                  Admin
                </Link>
              )}
              <button
                onClick={() => {
                  setIsMobileNavOpen(false);
                  signOutUser();
                  showToast('Signed out successfully.', 'info');
                  window.location.reload();
                }}
                className="nav-link"
              >
                Sign Out
              </button>
            </div>
          )}
        </nav>
      </header>

      <main>
        <section className="hero-section" id="home">
          <div className="hero-copy">
            <Reveal className="hero-eyebrow">Premium Print and Delivery</Reveal>
            <Reveal as="h1" className="hero-title" delay={100}>
              Your Docs,
              <br />
              <em>Perfectly</em>
              <br />
              Printed.
            </Reveal>
            <Reveal as="p" className="hero-description" delay={180}>
              Upload your files and let Art-Taste handle the print prep, finishing,
              and delivery. The storefront now runs as a React app with a matching
              admin dashboard inside this same folder.
            </Reveal>
            <Reveal className="hero-actions" delay={240}>
              <a href="#order" className="btn-primary">
                Start Printing
              </a>
              <a href="#how-it-works" className="btn-secondary">
                How It Works
              </a>
            </Reveal>
            <Reveal className="hero-stats" delay={320}>
              {heroStats.map((stat) => (
                <div key={stat.label} className="hero-stat-card">
                  <strong>{stat.value}</strong>
                  <span>{stat.label}</span>
                </div>
              ))}
            </Reveal>
          </div>

          <div className="hero-visual">
            <div className="hero-background-word">PRINT</div>
            <div className="paper-stack">
              <div className="paper paper-back" />
              <div className="paper paper-middle" />
              <div className="paper paper-front">
                <div className="paper-lines">
                  {Array.from({ length: 10 }).map((_, index) => (
                    <span key={`line-${index}`} className="paper-line" />
                  ))}
                </div>
                <div className="paper-stamp">ART TASTE</div>
              </div>
            </div>

            <div className="delivery-badge">
              <span>Express Delivery</span>
              <strong>Same Day</strong>
            </div>
          </div>
        </section>

        <div className="marquee-strip" aria-hidden="true">
          <div className="marquee-track">
            {[...marqueeItems, ...marqueeItems].map((item, index) => (
              <span key={`${item}-${index}`} className="marquee-item">
                {item}
              </span>
            ))}
          </div>
        </div>

        <section id="how-it-works" className="section-dark">
          <SectionHeading
            eyebrow="Simple Process"
            title="Four steps to your doorstep"
            subtitle="Upload, configure, approve, and let the fulfillment flow take over."
            light
          />

          <div className="workflow-grid">
            {workflowSteps.map((step, index) => (
              <Reveal key={step.number} className="workflow-card" delay={index * 70}>
                <span className="workflow-number">{step.number}</span>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </Reveal>
            ))}
          </div>
        </section>

        <section id="services" className="section-light">
          <SectionHeading
            eyebrow="What We Offer"
            title="Printing services with finish and speed"
            subtitle="From quick black-and-white jobs to full-color presentation work."
          />

          <div className="services-grid">
            {services.map((service, index) => (
              <Reveal key={service.title} className="service-card" delay={index * 70}>
                <span className="service-badge">{service.badge}</span>
                <h3>{service.title}</h3>
                <p>{service.description}</p>
                <small>{service.tag}</small>
              </Reveal>
            ))}
          </div>
        </section>

        <section id="order" className="section-order">
          <SectionHeading
            eyebrow="Place Your Order"
            title="Configure, upload, and pay"
            subtitle="Fill in your details first, then upload files, and finally complete payment."
          />

          <form className="order-grid" onSubmit={handleOrderSubmit}>
            <Reveal className="order-form-card">
              <div className="panel-header">
                <h3>Order Details</h3>
                <p>Customer details, files, and print settings.</p>
              </div>

              <div className="field-grid two-up">
                <label className="field">
                  <span>Full Name *</span>
                  <input
                    id="customer-name"
                    name="customer-name"
                    type="text"
                    value={orderForm.customer.name}
                    onChange={handleCustomerChange('name')}
                    placeholder="Rahul Sharma"
                    disabled={isSubmitting}
                  />
                  {fieldErrors.name ? <small className="field-error">{fieldErrors.name}</small> : null}
                </label>

                <label className="field">
                  <span>Phone *</span>
                  <input
                    id="customer-phone"
                    name="customer-phone"
                    type="tel"
                    value={orderForm.customer.phone}
                    onChange={handleCustomerChange('phone')}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength="10"
                    placeholder="9876543210"
                    disabled={isSubmitting}
                  />
                  {fieldErrors.phone ? <small className="field-error">{fieldErrors.phone}</small> : null}
                </label>
              </div>

              <label className="field">
                <span>Email *</span>
                <input
                  id="customer-email"
                  name="customer-email"
                  type="email"
                  value={orderForm.customer.email}
                  onChange={handleCustomerChange('email')}
                  placeholder="you@example.com"
                  disabled={isSubmitting}
                />
              </label>

              <label className="field">
                <span>Delivery Address *</span>
                <textarea
                  id="customer-address"
                  name="customer-address"
                  value={orderForm.customer.address}
                  onChange={handleCustomerChange('address')}
                  placeholder="House or flat, area, city, PIN"
                  rows="3"
                  disabled={isSubmitting}
                />
              </label>

              <div className="field">
                <span>Upload Files *</span>
                <FileDropzone
                  files={selectedFiles}
                  disabled={isSubmitting || isUploading || filesUploaded}
                  progress={uploadProgress}
                  onFilesAdd={addFiles}
                  onFileRemove={removeFile}
                />
                {!filesUploaded && (
                  <button
                    type="button"
                    className="btn-secondary full-width"
                    onClick={handleUploadFiles}
                    disabled={isUploading || selectedFiles.length === 0}
                    style={{ marginTop: '1rem' }}
                  >
                    {isUploading ? 'Uploading...' : 'Upload Files'}
                  </button>
                )}
                {filesUploaded && (
                  <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#f0f9ff', border: '1px solid #0ea5e9', borderRadius: '0.5rem' }}>
                    <p style={{ color: '#0ea5e9', margin: 0 }}>
                      ✓ Files uploaded successfully ({uploadedFiles.length} file{uploadedFiles.length !== 1 ? 's' : ''})
                    </p>
                  </div>
                )}
              </div>

              <div className="field-grid two-up">
                <label className="field">
                  <span>Print Type</span>
                  <select
                    id="print-type"
                    name="print-type"
                    value={orderForm.printOptions.type}
                    onChange={handlePrintOptionChange('type')}
                    disabled={isSubmitting}
                  >
                    {printTypeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="field">
                  <span>Paper Size</span>
                  <select
                    id="paper-size"
                    name="paper-size"
                    value={orderForm.printOptions.size}
                    onChange={handlePrintOptionChange('size')}
                    disabled={isSubmitting}
                  >
                    {paperSizeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="field">
                  <span>Copies</span>
                  <input
                    id="copies"
                    name="copies"
                    type="number"
                    min="1"
                    max={appConfig.maxCopies}
                    value={orderForm.printOptions.copies}
                    onChange={handlePrintOptionChange('copies')}
                    disabled={isSubmitting}
                  />
                </label>

                <label className="field">
                  <span>Pages (Auto-counted)</span>
                  <input
                    id="pages"
                    name="pages"
                    type="number"
                    value={orderForm.printOptions.pages}
                    readOnly
                    disabled
                  />
                </label>

                <label className="field">
                  <span>Binding</span>
                  <select
                    id="binding"
                    name="binding"
                    value={orderForm.printOptions.binding}
                    onChange={handlePrintOptionChange('binding')}
                    disabled={isSubmitting}
                  >
                    {bindingOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="field">
                  <span>Delivery</span>
                  <select
                    id="delivery"
                    name="delivery"
                    value={orderForm.printOptions.delivery}
                    onChange={handlePrintOptionChange('delivery')}
                    disabled={isSubmitting}
                  >
                    {deliveryOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <label className="field">
                <span>Special Instructions</span>
                <textarea
                  id="special-instructions"
                  name="special-instructions"
                  value={orderForm.notes}
                  onChange={handleNotesChange}
                  placeholder="Double-sided, lamination, or delivery notes"
                  rows="4"
                  disabled={isSubmitting}
                />
              </label>
            </Reveal>

            <Reveal className="order-summary-card" delay={120}>
              <div className="panel-header">
                <h3>Order Summary</h3>
                <p>Pricing updates instantly from your selected options.</p>
              </div>

              <div className="summary-row">
                <span>Print Type</span>
                <strong>{pricing.printType.label}</strong>
              </div>
              <div className="summary-row">
                <span>Paper Size</span>
                <strong>{pricing.paperSize.label}</strong>
              </div>
              <div className="summary-row">
                <span>Pages x Copies</span>
                <strong>
                  {pricing.pages} x {pricing.copies}
                </strong>
              </div>
              <div className="summary-row">
                <span>Print Cost</span>
                <strong>{formatCurrency(pricing.printCost)}</strong>
              </div>
              <div className="summary-row">
                <span>Binding</span>
                <strong>
                  {pricing.binding.price > 0
                    ? formatCurrency(pricing.binding.price)
                    : 'Included'}
                </strong>
              </div>
              <div className="summary-row">
                <span>Delivery</span>
                <strong>
                  {pricing.delivery.price > 0
                    ? formatCurrency(pricing.delivery.price)
                    : 'Free'}
                </strong>
              </div>

              <div className="summary-total">
                <span>Total</span>
                <strong>{formatCurrency(pricing.total)}</strong>
              </div>

              <p className="summary-note">
                Orders placed before noon are the best fit for same-day delivery.
                Standard delivery generally arrives within one to two business days.
              </p>

              <button type="submit" className="btn-primary full-width" disabled={isSubmitting || !filesUploaded}>
                {isSubmitting ? 'Processing Payment...' : 'Pay & Confirm Order'}
              </button>
            </Reveal>
          </form>
        </section>

        <section id="pricing" className="section-warm">
          <SectionHeading
            eyebrow="Transparent Rates"
            title="Simple pricing plans"
            subtitle="The calculator above uses the exact same pricing rules shown here."
          />

          <div className="pricing-grid">
            {pricingPlans.map((plan, index) => (
              <Reveal
                key={plan.name}
                className={`pricing-card ${plan.featured ? 'featured' : ''}`}
                delay={index * 70}
              >
                {plan.badge ? <span className="pricing-badge">{plan.badge}</span> : null}
                <h3>{plan.name}</h3>
                <p>{plan.description}</p>
                <div className="pricing-value">{plan.price}</div>
                <span className="pricing-suffix">{plan.priceSuffix}</span>
                <ul className="pricing-feature-list">
                  {plan.features.map((feature) => (
                    <li key={feature}>{feature}</li>
                  ))}
                </ul>
                <a
                  href={plan.cta === 'Contact Us' ? '#contact' : '#order'}
                  className={plan.featured ? 'btn-primary full-width' : 'btn-secondary full-width'}
                >
                  {plan.cta}
                </a>
              </Reveal>
            ))}
          </div>
        </section>

        <section id="testimonials" className="section-light">
          <SectionHeading
            eyebrow="What People Say"
            title="Trusted by students, teams, and creators"
          />

          <div className="testimonial-grid">
            {testimonials.map((testimonial, index) => (
              <Reveal key={testimonial.name} className="testimonial-card" delay={index * 80}>
                <p>{testimonial.quote}</p>
                <div className="testimonial-footer">
                  <span>{testimonial.initial}</span>
                  <div>
                    <strong>{testimonial.name}</strong>
                    <small>{testimonial.role}</small>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        <section id="store" className="store-section">
          <div className="store-background-word">STORE</div>
          <Reveal className="store-content">
            <span className="store-kicker">Coming Soon</span>
            <h2>
              Our <em>store</em> is opening
            </h2>
            <p>
              Ready-made templates, premium stationery packs, and curated print
              bundles will live here next.
            </p>

            <div className="countdown-grid">
              <div className="countdown-card">
                <strong>{countdown.days}</strong>
                <span>Days</span>
              </div>
              <div className="countdown-card">
                <strong>{countdown.hours}</strong>
                <span>Hours</span>
              </div>
              <div className="countdown-card">
                <strong>{countdown.minutes}</strong>
                <span>Minutes</span>
              </div>
              <div className="countdown-card">
                <strong>{countdown.seconds}</strong>
                <span>Seconds</span>
              </div>
            </div>

            <form className="notify-form" onSubmit={handleNotifySubmit}>
              <input
                type="email"
                value={notifyEmail}
                onChange={(event) => setNotifyEmail(event.target.value)}
                placeholder="Enter your email for launch updates"
                disabled={notifyLocked}
              />
              <button type="submit" className="btn-primary" disabled={notifyLocked}>
                {notifyLocked ? 'Subscribed' : 'Notify Me'}
              </button>
            </form>
          </Reveal>
        </section>

        <section id="contact" className="section-light">
          <SectionHeading
            eyebrow="Contact Us"
            title="Reach Art-Taste directly"
            subtitle="Call or email us and we will help with bulk orders, business pricing, or custom print requests."
          />

          <div className="services-grid">
            <Reveal className="service-card">
              <span className="service-badge">CALL</span>
              <h3>Phone</h3>
              <p>
                <a href="tel:9817235506">9817235506</a>
              </p>
              <small>Available for order and support queries</small>
            </Reveal>

            <Reveal className="service-card" delay={80}>
              <span className="service-badge">MAIL</span>
              <h3>Email</h3>
              <p>
                <a href="mailto:ArtTaste551@gmail.com">ArtTaste551@gmail.com</a>
              </p>
              <small>Best for bulk and business requests</small>
            </Reveal>

            <Reveal className="service-card" delay={80}>
              <span className="service-badge">MAIL</span>
              <h3>Email</h3>
              <p>
                <a href="mailto:ConnectAkash@proton.me">ConnectAkash@proton.me</a>
              </p>
              <small>Contact for website related issues and queries</small>
            </Reveal>
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <div>
          <div className="brand-mark footer-brand">
            Art<span>-</span>Taste
          </div>
          <p>
            Premium print and delivery from {appConfig.city}.
          </p>
        </div>

        <div className="footer-links">
          <a href="#home">Home</a>
          <a href="#services">Services</a>
          <a href="#pricing">Pricing</a>
          <a href="#order">Order</a>
          <a href="#contact">Contact</a>
          {currentUser?.role === 'admin' ? <Link to="/admin">Admin</Link> : null}
        </div>
      </footer>

      <OrderSuccessModal
        isOpen={successState.isOpen}
        orderId={successState.orderId}
        paymentMode={successState.paymentMode}
        onClose={() =>
          setSuccessState({
            isOpen: false,
            orderId: '',
            paymentMode: 'demo',
          })
        }
      />
    </div>
  );
}
